import {
  AssignmentInd,
  BusinessCenter,
  Info,
  LocationOn,
  OpenInNew,
  Person,
} from '@mui/icons-material';
import { Box, Chip, Grid, Paper, Typography } from '@mui/material';
import { IconButton } from '@mui/material';
import InfoCard from 'lib/designSystem/InfoCard';
import { parseRutWithDots, showAlert } from 'lib/functions/utils';
import { Warranty } from 'lib/types/models';
import React from 'react';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { showBusinessNetwork } from 'services/redux/businessNetworkSlice';

interface Props {
  warranty: Warranty;
}

const PrincipalInfo: React.FC<Props> = ({ warranty }) => {
  const dispatch = useDispatch();

  const handleShowBusinessDetails = () => {
    if (!warranty.principal || !warranty.principalRut) {
      showAlert(
        dispatch,
        'error',
        'No se puede mostrar los detalles del negocio',
      );
      return;
    }
    dispatch(
      showBusinessNetwork({
        id: warranty.principal?.primary,
        name: warranty.principal?.secondary,
      }),
    );
  };

  const identifier = warranty.principalRut;
  const profileUrl = useMemo(() => {
    const formattedRut = parseRutWithDots(identifier);
    if (!formattedRut) return null;
    return `https://comprador.mercadopublico.cl/ficha/informacion-general/${formattedRut}`;
  }, [identifier]);

  const unitCodeValue = warranty?.unit
    ? `${warranty.unit.name ?? 'N/A'} (${warranty.unit.unitCode ?? 'N/A'})`
    : 'N/A';

  return (
    <Paper sx={{ p: 1.5, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 1,
          mb: 1.5,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            fontSize: '1rem',
            lineHeight: 1.1,
          }}
        >
          Información del Mandante
        </Typography>
        <Chip
          label="Detalles"
          size="small"
          color="primary"
          variant="outlined"
          sx={{ height: '18px', fontSize: '0.65rem' }}
        />
        {profileUrl && (
          <IconButton
            component="a"
            href={profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            aria-label="Abrir mandante"
            sx={{ p: 0.25 }}
          >
            <OpenInNew sx={{ fontSize: '0.9rem' }} />
          </IconButton>
        )}
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6} display="flex" alignItems="center" gap={1}>
          <InfoCard
            icon={<Person />}
            label="Razón Social"
            value={warranty.principal?.primary ?? 'Datos no disponibles'}
            color="blue"
          />
          <IconButton
            size="small"
            aria-label="Ver detalles del mandante"
            onClick={handleShowBusinessDetails}
          >
            <Info fontSize="small" />
          </IconButton>
        </Grid>

        <Grid item xs={12} md={6}>
          <InfoCard
            icon={<AssignmentInd />}
            label="RUT"
            value={identifier ?? 'Datos no disponibles'}
            color="blue"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <InfoCard
            icon={<LocationOn />}
            label="Dirección"
            value={warranty.address ?? 'Datos no disponibles'}
            color="orange"
          />
        </Grid>

        {warranty.unit && (
          <Grid item xs={12} md={6}>
            <InfoCard
              icon={<BusinessCenter />}
              label="Unidad de Negocio"
              value={unitCodeValue}
              color="purple"
            />
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

export default PrincipalInfo;
